import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Newspaper, Handshake, Trophy, ShieldCheck, LogOut, House, MessageCircleQuestion,
} from "lucide-react";

// macOS-style app shell: Finder-like translucent sidebar on desktop,
// compact toolbar on mobile. Wraps Challenges / Admin / Profile.
export default function AppShell({ children }) {
  const { user, login, logout } = useAuth();
  const loc = useLocation();

  const items = [
    { to: "/challenges", label: "Challenges", icon: Trophy, match: (p) => p === "/challenges" },
    { to: "/news", label: "Journal", icon: Newspaper, match: (p) => p.startsWith("/news") },
    { to: "/faq", label: "FAQ", icon: MessageCircleQuestion, match: (p) => p === "/faq" },
    { to: "/partner", label: "Partner", icon: Handshake, match: (p) => p === "/partner" },
    { to: "/", label: "Studio", icon: House, match: (p) => p === "/" || p.startsWith("/games") },
  ];
  if (user?.is_admin) {
    items.push({ to: "/admin", label: "Admin", icon: ShieldCheck, match: (p) => p === "/admin" });
  }

  const linkCls = (active) =>
    `flex items-center gap-3 px-3 py-2 rounded-[10px] text-[13px] transition-colors ${
      active ? "bg-white/10 text-white" : "text-white/55 hover:text-white hover:bg-white/[0.04]"
    }`;

  return (
    <div className="relative min-h-screen text-white md:flex">
      {/* Sidebar — desktop */}
      <aside className="hidden md:flex w-60 shrink-0 flex-col fixed inset-y-0 left-0 z-40 border-r border-white/[0.07] bg-black/30 backdrop-blur-2xl px-4 py-6">
        <Link to="/" className="flex items-center gap-2.5 px-2">
          <img src="/logo.png" alt="Gumble" className="h-6 w-auto" />
        </Link>

        <div className="mt-8 px-3 text-[11px] font-medium tracking-wide text-white/35">Studio</div>
        <nav className="mt-2 space-y-0.5">
          {items.map((it) => (
            <Link key={it.to + it.label} to={it.to} className={linkCls(it.match(loc.pathname))}>
              <it.icon className="w-4 h-4 shrink-0" />
              {it.label}
            </Link>
          ))}
        </nav>

        <div className="mt-auto space-y-3">
          <div className="rounded-2xl border border-white/[0.07] bg-white/[0.03] p-3.5">
            <div className="text-[12px] text-white/60 leading-relaxed">
              {user?.stake_username ? (
                <>Linked as <span className="text-white">@{user.stake_username}</span></>
              ) : (
                <>Link Stake to earn crates from 100x+ hits.</>
              )}
            </div>
            {!user?.stake_username && (
              <Link to="/challenges" className="mt-2.5 block text-center rounded-full bg-[#0A84FF] text-white text-[12px] font-semibold py-2 hover:bg-[#0077ED] transition-colors">
                Get started
              </Link>
            )}
          </div>
          {user ? (
            <div className="flex items-center gap-2.5 px-2">
              {user.avatar
                ? <img src={user.avatar} alt="" className="w-7 h-7 rounded-full" />
                : <span className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-[12px] text-white/70">{(user.global_name || user.username || "?")[0]}</span>}
              <div className="min-w-0 flex-1">
                <div className="text-[13px] truncate">{user.global_name || user.username}</div>
                <button onClick={logout} className="text-[11px] text-white/40 hover:text-white/70 inline-flex items-center gap-1">
                  <LogOut className="w-3 h-3" /> Sign out
                </button>
              </div>
            </div>
          ) : (
            <button onClick={login} className="w-full rounded-full bg-white text-black text-[13px] font-semibold py-2.5 hover:bg-white/85 transition-colors">
              Sign in
            </button>
          )}
        </div>
      </aside>

      {/* Toolbar — mobile */}
      <div className="md:hidden sticky top-0 z-40 border-b border-white/[0.07] bg-black/50 backdrop-blur-2xl">
        <div className="flex items-center gap-2 px-4 py-3 overflow-x-auto">
          <Link to="/" className="shrink-0 mr-1">
            <img src="/logo.png" alt="Gumble" className="h-5 w-auto" />
          </Link>
          {items.map((it) => (
            <Link key={it.to + it.label} to={it.to}
              className={`shrink-0 px-3 py-1.5 rounded-full text-[12.5px] ${it.match(loc.pathname) ? "bg-white text-black font-semibold" : "text-white/60"}`}>
              {it.label}
            </Link>
          ))}
          <span className="flex-1" />
          {user
            ? <button onClick={logout} className="shrink-0 text-white/50"><LogOut className="w-4 h-4" /></button>
            : <button onClick={login} className="shrink-0 px-3 py-1.5 rounded-full bg-white text-black text-[12px] font-semibold">Sign in</button>}
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 min-w-0 md:ml-60 relative z-10">
        {children}
      </main>
    </div>
  );
}
